'use client'

import {
  AriaAttributes,
  ChangeEvent,
  FormEvent,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useState,
} from 'react'
import { Button, Divider, Icon, InputBar, Validation } from '@vinted/web-ui'
import { compact, isEmpty, trim } from 'lodash'
import classNames from 'classnames'
import { ArrowRight16 } from '@vinted/monochrome-icons'

import { useDataDomeCaptcha } from '@marketplace-web/bot-detection/data-dome-feature'
import { ShowAtBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { KeyboardKey } from '@marketplace-web/vendor-abstractions/web-ui-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import {
  ComposerType,
  conversationPasteEvent,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { MediaUploadPhotoDto } from '@marketplace-web/item-upload/media-upload-data'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { PersonalInformationSharingModal } from '@marketplace-web/personal-information/personal-information-feature'

import usePostReply from '../../hooks/usePostReply/usePostReply'
import { PersonalInformationSharingContentType } from '../../constants/personal-information-sharing'

import ComposerActions from './ComposerActions'
import ImagePreview from './ImagePreview'
import { isDateInTheFuture } from '../../utils/date'
import HolidayNote from '../HolidayNote'

type OnPostReplyArgs = {
  id: string | number
  title: string
  updated_at: string
}

type Props = {
  type: ComposerType
  onPostReply?: (args: OnPostReplyArgs) => void
}

const Composer = ({ type, onPostReply }: Props) => {
  const translate = useTranslate('conversation.composer')
  const { postReplyRequest } = usePostReply()

  const { user } = useSession()
  const { conversation, transaction, sendMessageUi } = useConversationContext()
  const conversationId = conversation?.id
  const userId = user?.id
  const { track } = useTracking()

  const [value, setValue] = useState<string | null | undefined>(null)
  const [isInputExpanded, setIsInputExpanded] = useState(false)
  const [isUploaderActive, setIsUploaderActive] = useState(false)
  const [photoTempUuids, setPhotoTempUuids] = useState<Array<string>>([])
  const [photos, setPhotos] = useState<Array<MediaUploadPhotoDto>>([])
  const [actionError, setActionError] = useState<ReactNode>(null)
  const [inputElement, setInputElement] = useState<HTMLTextAreaElement | HTMLInputElement | null>(
    null,
  )
  const [personalInformationSharingContentType, setPersonalInformationSharingContentType] =
    useState<PersonalInformationSharingContentType | null>(null)

  useEffect(() => {
    if (sendMessageUi.uiState !== UiState.Success) return
    setIsUploaderActive(false)
    setValue(null)
    setPhotoTempUuids([])
    setPhotos([])
    setActionError(null)
  }, [sendMessageUi])

  useEffect(() => {
    if (!inputElement || !conversationId) return undefined

    const handlePaste = () => {
      track(conversationPasteEvent(String(conversationId)))
    }

    inputElement.addEventListener('paste', handlePaste)

    return () => {
      inputElement.removeEventListener('paste', handlePaste)
    }
  }, [conversationId, inputElement, track])

  const isReplyAllowed = Boolean(conversation?.isReplyAllowed)

  const handleOnImageRemove = (imageToRemove: MediaUploadPhotoDto) => () => {
    const newImages = photos.filter(photo => photo !== imageToRemove)

    setPhotos(newImages)
  }

  const handleOnChange = (event: ChangeEvent<HTMLInputElement> | FormEvent<HTMLTextAreaElement>) =>
    setValue(event.currentTarget.value)

  const toggleInputExpansion = () => setIsInputExpanded(!isInputExpanded)

  const isInputDisabled = sendMessageUi.uiState === UiState.Pending || isUploaderActive

  const isSubmitDisabled =
    !userId || isInputDisabled || (isEmpty(trim(value ?? '')) && isEmpty(photos))

  const getPhotoTempUuids = (): Array<string> | null => {
    if (isEmpty(photoTempUuids) && isEmpty(photos)) return null
    if (isEmpty(photos)) return photoTempUuids

    return compact(photos.map(photo => photo.temp_uuid))
  }

  const handlePostReply = (isPersonalDataSharingCheckSkipped = false) => {
    postReplyRequest({
      value,
      photoTempUuids: getPhotoTempUuids(),
      isPersonalDataSharingCheckSkipped,
      onSetPersonalInformationSharingContentType: setPersonalInformationSharingContentType,
    })

    if (!conversation || !value || !onPostReply) return

    onPostReply({
      id: conversation.id,
      title: value,
      updated_at: new Date(Date.now()).toISOString(),
    })
  }

  const handlePostForceReply = () => {
    handlePostReply(true)
  }

  const handleSubmit = () => {
    if (type === ComposerType.Reply) handlePostReply()
  }

  const handleOnKeyDown = (event: KeyboardEvent): void => {
    if (event.shiftKey || event.key !== KeyboardKey.Enter) return

    if (isSubmitDisabled) {
      event.preventDefault()

      return
    }

    handleSubmit()
  }

  const storeImage = (image: MediaUploadPhotoDto) => {
    setPhotos(previous => [...previous, image])
    setActionError(null)
  }

  const handleActionError = (error: string | JSX.Element) => setActionError(error)

  const renderError = () => {
    const error = actionError || sendMessageUi.error

    if (!error) return null

    return (
      <div className="u-ui-padding-top-regular">
        <Validation text={error} theme="warning" />
      </div>
    )
  }

  useDataDomeCaptcha(() => {
    handleSubmit()
  })

  const renderImagePreview = () => {
    if (isEmpty(photos)) return null

    return (
      <div className="u-ui-padding-top-regular">
        <ImagePreview images={photos} onImageRemove={handleOnImageRemove} />
      </div>
    )
  }

  const isConversationUploadEnabled = () => type === ComposerType.Reply

  const renderActions = () => {
    const isSellerCountdownActive = isDateInTheFuture(transaction?.seller?.closetCountdownEndDate)

    return (
      <ComposerActions
        isInputExpanded={isInputExpanded}
        isSellerCountdownActive={isSellerCountdownActive}
        storeImage={storeImage}
        onError={handleActionError}
        isConversationUploadEnabled={isConversationUploadEnabled()}
      />
    )
  }

  const renderSubmitButton = () => {
    const submitColor = isSubmitDisabled ? Icon.Color.GreyscaleLevel2 : Icon.Color.Primary

    let ariaAttributes: AriaAttributes

    if (isSubmitDisabled) {
      ariaAttributes = {
        'aria-hidden': true,
      }
    } else {
      ariaAttributes = {
        'aria-label': translate('a11y.send_message'),
      }
    }

    return (
      <Button
        inline
        styling={Button.Styling.Flat}
        theme="muted"
        icon={<Icon name={ArrowRight16} testId="arrow-right" color={submitColor} />}
        onClick={handleSubmit}
        disabled={isSubmitDisabled}
        aria={ariaAttributes}
      />
    )
  }

  const renderComposerInput = () => (
    <InputBar
      isMultiline
      name="composerInput"
      placeholder={translate('reply_placeholder')}
      value={value}
      disabled={isInputDisabled}
      onChange={handleOnChange}
      onFocus={toggleInputExpansion}
      onBlur={toggleInputExpansion}
      suffix={renderSubmitButton()}
      onKeyDown={handleOnKeyDown}
      forwardedRef={setInputElement}
      testId="composer"
    />
  )

  const renderComposerTabletsUp = ({ padding }: { padding: 'vertical' | 'bottom' }) => {
    const composerClass = classNames(
      'u-flexbox',
      'u-flex-direction-row',
      `u-ui-padding-${padding}-regular`,
      'u-ui-padding-horizontal-large',
    )
    const actionsClass = classNames(
      'u-flexbox',
      'u-flex-direction-column',
      'u-justify-content-flex-end',
    )
    const containerClass = classNames(
      'u-flexbox',
      'u-flex-direction-column',
      'u-fill-width',
      'u-min-width-none',
      'u-justify-content-center',
    )

    return (
      <div className={composerClass}>
        <div className={actionsClass}>{renderActions()}</div>
        <div className={containerClass}>
          {renderError()}
          {renderImagePreview()}
          {renderComposerInput()}
        </div>
      </div>
    )
  }

  const renderComposerPhones = () => {
    const composerClass = classNames('u-flexbox', 'u-ui-padding-bottom-regular')

    return (
      <div className="u-ui-padding-horizontal-large">
        {renderError()}
        {renderImagePreview()}
        <div className={composerClass}>
          {renderActions()}
          <div className="u-fill-width">{renderComposerInput()}</div>
        </div>
      </div>
    )
  }

  if (!isReplyAllowed) return null

  const renderHolidayNote = () => {
    const isOnHoliday = conversation?.oppositeUser?.isOnHoliday

    if (!isOnHoliday) return null

    return <HolidayNote />
  }

  const holidayNote = renderHolidayNote()

  return (
    <>
      <Divider />
      {holidayNote}
      <ShowAtBreakpoint breakpoint="phones">{renderComposerPhones()}</ShowAtBreakpoint>
      <ShowAtBreakpoint breakpoint="tabletsUp">
        {renderComposerTabletsUp({ padding: holidayNote ? 'bottom' : 'vertical' })}
      </ShowAtBreakpoint>
      {personalInformationSharingContentType ? (
        <PersonalInformationSharingModal
          threadId={conversationId ? conversationId.toString() : ''}
          contentType={personalInformationSharingContentType}
          onSetPersonalInformationSharingContentType={setPersonalInformationSharingContentType}
          onSendClick={handlePostForceReply}
        />
      ) : null}
    </>
  )
}

export default Composer
